import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  getInitialOnboardingTest,
  getInitialTestById,
  submitInitialResults,
  getTestData,
  submitTestResults,
  submitQuestionBankComplete,
  reattemptTest,
} from "../helper/apicalls/test";
import {
  getOneQuestionBank,
  getQuestionBankResult,
  submitQuestionBankResult,
  getOneQuestionBankInstitution,
  getQuestionBankInstitutionResult,
  submitQuestionBankInstitutionResult,
} from "../helper/apicalls/questionBank";
import {
  getHomeworkById,
  submitHomework,
  getCompletedHomework,
} from "../helper/apicalls/homeworks";
import {
  submitActiveRecallCompleted,
  getActiveRecallCompletedApi,
} from "../helper/apicalls/activeRecall";
import Cookies from "js-cookie";
import MCQ from "../components/MCQ";
import TrueFalse from "../components/TrueFalse";
import Match from "../components/Match";
import Fillups from "../components/Fillups";
import PsychologyIcon from "@mui/icons-material/Psychology";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ConversationalAnalysis from "../components/ConversationalAnalysis";
import AvailableTestCards from "../components/AvailableTestCards";
import ConfirmationPopup from "../components/confirmationPopup";
import "../styles/TestPage.scss";
import GlobalLoader from "../components/GlobalLoader";
import QuestionPalette from "../components/QuestionPalette";
import notAvailable from "../assets/images/notAvalaible.svg";
import { updateCompletedCourseApi } from "../helper/apicalls/course";
import {
  updateUserCoins,
  updateUserXP,
  checkIsAdmin,
} from "../helper/authUtils";
import { submitAdaptiveLearningTest } from "../helper/apicalls/adaptiveLearning";

const TEST_DURATION_MINUTES = 30;

const TestPage = () => {
  const { pageid, title, key } = useParams();
  const [searchParams] = useSearchParams();
  const testPageId = searchParams.get("test_page_content_id") || pageid;
  const keys = searchParams.get("key") || key;
  const titleName = searchParams.get("title") || title;
  const retest = searchParams.get("retest") || false;
  const testId = searchParams.get("test_id");
  const aiTestId = searchParams.get("ai_test_id");
  const homeworkId = searchParams.get("homework_id");
  const homeworkLevel = searchParams.get("level");
  const submittedRaw = searchParams.get("submitted");

  const homeworkSubmitted = submittedRaw === "true";
  const navigate = useNavigate();
  const location = useLocation();
  const isActiveRecall = searchParams.get("active-recall") === "true";
  const parentId =
    searchParams.get("parent_id") || searchParams.get("active_recall_id");
  const isPracticeMode = searchParams.get("practice") === "true";
  const isReviewParam = searchParams.get("review") === "true";
  const [questions, setQuestionsState] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndexState] = useState(0);
  const isSubmittedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState();
  const [timeDuration, setTimeDuration] = useState();
  const [testMetadata, setTestMetadata] = useState(null);
  const [isTestStarted, setIsTestStarted] = useState(false);
  const [initialTestCompleted, setInitialTestCompleted] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [testAnswers, setTestAnswers] = useState({});
  const [testScore, setTestScore] = useState(0);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showTestComplete, setShowTestComplete] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showPracticeResults, setShowPracticeResults] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [availableTests, setAvailableTests] = useState([]);
  const [showTestSelection, setShowTestSelection] = useState(false);
  const [visitedQuestions, setVisitedQuestions] = useState(new Set([0]));
  const timerRef = useRef(null);
  const isAdmin = checkIsAdmin();

  const messageText = isPracticeMode
    ? "Are you sure you want to leave the practice test?"
    : testPageId
      ? "Are you sure you want to leave the test?"
      : "Are you sure you want to leave the test? You will be logged out.";

  const normalizeOptions = (options) => {
    if (!options) return {};
    if (!Array.isArray(options)) return options;
    return options.reduce((acc, curr) => {
      const key = Object.keys(curr)[0];
      acc[key] = curr[key];
      return acc;
    }, {});
  };

  useEffect(() => {
    const fetchTestData = async () => {
      try {
        setLoading(true);
        setQuestionsState([]);
        setCurrentQuestionIndexState(0);

        if (
          location.state?.isAdaptiveTest &&
          isReviewParam &&
          Array.isArray(location.state?.data)
        ) {
          const attemptData = location.state.data;

          if (!attemptData.length) {
            setError("No attempt data available for review.");
            setLoading(false);
            return;
          }

          const reconstructedQuestions = attemptData.map((item, idx) => {
            let type = item?.type || "mcq";
            if (
              type === "mcq" &&
              (!item.options || item.options.length === 0) &&
              (String(item.answer || "").toLowerCase() === "true" ||
                String(item.answer || "").toLowerCase() === "false")
            ) {
              type = "trueorfalse";
            }

            return {
              sequence: item.sequence || idx + 1,
              type: type,
              content: {
                question_id: item.sequence || idx + 1,
                question: item.question,
                options: normalizeOptions(item.options),
                pairs: item.pairs || [],
                answer: item.answer,
                explanation: item.explanation,
                hint: item.hint,
                user_answer: item.user_answer,
              },
            };
          });

          const answersMap = {};
          attemptData.forEach((item, idx) => {
            let ans = item.user_answer;
            const type = item.type || "mcq";
            if (ans !== undefined && ans !== null) {
              if (type === "trueorfalse" || type === "truefalse") {
                if (String(ans).toLowerCase() === "true") ans = "A";
                else if (String(ans).toLowerCase() === "false") ans = "B";
                else if (String(ans).toLowerCase() === "a") ans = "A";
                else if (String(ans).toLowerCase() === "b") ans = "B";
              } else if (type === "mcq" || type === "quiz") {
                if (typeof ans === "string") {
                  ans = ans.toUpperCase();
                }
              }
              answersMap[item.sequence || idx + 1] = ans;
            }
          });
          setQuestionsState(reconstructedQuestions);
          setTestAnswers(answersMap);
          setTestMetadata({
            id: `adaptive-review-${Date.now()}`,
            name: "Adaptive Test Review",
            duration: 0,
          });

          if (attemptData.no_of_question_correct !== undefined) {
            setTestScore(attemptData.no_of_question_correct);
          }

          isSubmittedRef.current = true;
          setIsTestStarted(true);
          setShowAnswers(true);
          setTimeRemaining(0);
          setLoading(false);
          return;
        }

        if (
          location.state?.isAdaptiveTest &&
          !isReviewParam &&
          Array.isArray(location.state?.data)
        ) {
          const adaptiveQuestions = location.state.data;

          if (!adaptiveQuestions.length) {
            setError("No questions available for this adaptive test.");
            setLoading(false);
            return;
          }

          const duration = TEST_DURATION_MINUTES;
          setTestMetadata({
            id: `adaptive-${Date.now()}`,
            name: location.state.title || "Adaptive Test",
            duration,
            passPercentage: 0,
            isAdaptive: true,
          });

          setTimeDuration(duration);
          setTimeRemaining(duration * 60);
          setQuestionsState(adaptiveQuestions);
          setLoading(false);
          return;
        }

        if (aiTestId) {
          const aiTestData = Cookies.get(`ai_test_${aiTestId}`);
          if (aiTestData) {
            try {
              const parsedTestData = JSON.parse(aiTestData);
              const questionsArray = parsedTestData.questions || [];

              if (questionsArray.length > 0) {
                setTestMetadata({
                  id: aiTestId,
                  name: parsedTestData.name || "AI Generated Test",
                  duration: parsedTestData.duration || 30,
                  passPercentage: 70,
                  isAiGenerated: true,
                  difficulty: parsedTestData.difficulty,
                  questionBankId: parsedTestData.questionBankId,
                });

                const duration =
                  parsedTestData.duration || TEST_DURATION_MINUTES;
                setTimeDuration(duration);
                setTimeRemaining(duration * 60);
                setQuestionsState(questionsArray);

                setLoading(false);
                return;
              }
            } catch (e) {
              console.error("Error parsing AI test data:", e);
              setError("Failed to load AI-generated test.");
              setLoading(false);
              return;
            }
          } else {
            setError("AI-generated test not found.");
            setLoading(false);
            return;
          }
        }

        let response;
        let responseData;

        if (homeworkId) {
          let hwResponse;
          if (homeworkSubmitted) {
            hwResponse = await getCompletedHomework(homeworkId, homeworkLevel);
          } else {
            hwResponse = await getHomeworkById(homeworkId, homeworkLevel);
          }
          const hwData = hwResponse?.data || hwResponse;

          const hwResult = hwData?.data || hwData;
          if (
            homeworkSubmitted &&
            hwResult?.attempt_data &&
            Array.isArray(hwResult.attempt_data)
          ) {
            const reconstructedQuestions = hwResult.attempt_data.map(
              (item, idx) => {
                let type = item.type || "mcq";

                if (
                  type === "mcq" &&
                  (!item.options || item.options.length === 0) &&
                  (String(item.answer || "").toLowerCase() === "true" ||
                    String(item.answer || "").toLowerCase() === "false")
                ) {
                  type = "truefalse";
                }

                if (Array.isArray(item.pairs) && item.pairs.length > 0) {
                  type = "match";
                }

                const sequence = item.sequence || idx + 1;

                return {
                  sequence,
                  type,
                  content: {
                    question_id: sequence,
                    question: item.question,
                    options: item.options,
                    pairs: item.pairs || [],
                    answer: item.answer,
                    explanation: item.explanation,
                    hint: item.hint,
                  },
                };
              },
            );

            const answersMap = {};
            hwResult.attempt_data.forEach((item, idx) => {
              const sequence = item.sequence || idx + 1;
              let type = item.type || "mcq";

              if (Array.isArray(item.pairs) && item.pairs.length > 0) {
                type = "match";
              } else if (
                type === "mcq" &&
                (!item.options || item.options.length === 0) &&
                (String(item.answer || "").toLowerCase() === "true" ||
                  String(item.answer || "").toLowerCase() === "false")
              ) {
                type = "truefalse";
              }

              let ans = item.user_answer;
              if (ans !== undefined && ans !== null) {
                if (type === "trueorfalse" || type === "truefalse") {
                  if (String(ans).toLowerCase() === "true") ans = "A";
                  else if (String(ans).toLowerCase() === "false") ans = "B";
                  else if (String(ans).toLowerCase() === "a") ans = "A";
                  else if (String(ans).toLowerCase() === "b") ans = "B";
                } else if (type === "mcq" || type === "quiz") {
                  if (typeof ans === "string") {
                    ans = ans.toUpperCase();
                  }
                }
                answersMap[sequence] = ans;
              }
            });

            setQuestionsState(reconstructedQuestions);
            setTestAnswers(answersMap);
            setTestMetadata({
              id: hwResult.id || homeworkId,
              name: hwResult.name || hwResult.title || "Homework Review",
              duration: hwResult.duration || TEST_DURATION_MINUTES,
            });

            isSubmittedRef.current = true;
            setTimeRemaining(0);
            setIsTestStarted(true);
            setLoading(false);
            return;
          }

          let content =
            hwData?.questions || hwData?.mcq || hwData?.content || [];

          if (typeof content === "string") {
            try {
              content = JSON.parse(content);
            } catch (e) {
              console.error("Error parsing homework content:", e);
              content = [];
            }
          }

          const rawQuestionsArray = Array.isArray(content)
            ? content
            : Array.isArray(content?.questions)
              ? content.questions
              : [];

          if (!rawQuestionsArray.length) {
            throw new Error("No questions found in homework");
          }
          const questionsArray = rawQuestionsArray.map((q, idx) => ({
            ...q,
            sequence: idx + 1,
            _originalSequence: q.sequence,
          }));

          const duration = TEST_DURATION_MINUTES;
          setTestMetadata({
            id: hwData.id || homeworkId,
            name: hwData.title || hwData.name || "Homework Test",
            duration,
            passPercentage: hwData.pass_percentage,
          });

          setTimeDuration(duration);
          setTimeRemaining(duration * 60);
          setQuestionsState(questionsArray);
          setLoading(false);
          return;
        }

        if (testId) {
          response = await getInitialTestById(testId);
          responseData = response?.data || response;

          let content = responseData?.content;
          if (typeof content === "string") {
            try {
              content = JSON.parse(content);
            } catch (e) {
              console.error("Error parsing test content:", e);
              content = [];
            }
          }

          let questionsArray = [];
          if (Array.isArray(responseData?.questions)) {
            questionsArray = responseData.questions;
          } else if (Array.isArray(content)) {
            questionsArray = content;
          } else if (content && Array.isArray(content.questions)) {
            questionsArray = content.questions;
          }

          if (questionsArray && questionsArray.length > 0) {
            setTestMetadata({
              id: responseData.id,
              name: responseData.name || responseData.title || "Test",
              duration: responseData.duration || 30,
              passPercentage: responseData.pass_percentage,
              course: responseData.course,
              isActive: responseData.is_active,
              pageType: responseData.page_type,
              testType: responseData.test_type,
            });

            const duration = responseData.duration || TEST_DURATION_MINUTES;
            setTimeDuration(duration);
            setTimeRemaining(duration * 60);
            setQuestionsState(questionsArray);
            return;
          } else {
            throw new Error("No questions found in test");
          }
        }

        if (isPracticeMode && pageid) {
          const folder_id =
            searchParams.get("parent_id") ||
            location.state?.questionBank?.questionbank_folders ||
            location.state?.questionBank?.questionbank_folder;

          const isInstitution = searchParams.get("type") === "institution";
          let response;

          if (isReviewParam) {
            response = isInstitution
              ? await getQuestionBankInstitutionResult({
                  questionbank_folders: folder_id,
                  question_bank: pageid,
                })
              : await getQuestionBankResult({
                  questionbank_folders: folder_id,
                  question_bank: pageid,
                });

            const resultData = response?.data || response;

            if (
              resultData?.attempt_data &&
              Array.isArray(resultData.attempt_data)
            ) {
              const reconstructedQuestions = resultData.attempt_data.map(
                (item, idx) => {
                  let type = item.type || "mcq";
                  if (
                    type === "mcq" &&
                    (!item.options || item.options.length === 0) &&
                    (String(item.answer || "").toLowerCase() === "true" ||
                      String(item.answer || "").toLowerCase() === "false")
                  ) {
                    type = "truefalse";
                  }

                  return {
                    sequence: item.sequence || idx + 1,
                    type: type,
                    isCorrect: item.isCorrect,
                    content: {
                      question_id: item.sequence || idx + 1,
                      question: item.question,
                      options: item.options,
                      pairs: item.pairs || [],
                      answer: item.answer,
                      explanation: item.explanation,
                      hint: item.hint,
                    },
                  };
                },
              );

              const answersMap = {};
              resultData.attempt_data.forEach((item, idx) => {
                let ans = item.user_answer;
                const type = item.type || "mcq";
                if (ans !== undefined && ans !== null) {
                  if (type === "trueorfalse" || type === "truefalse") {
                    if (String(ans).toLowerCase() === "true") ans = "A";
                    else if (String(ans).toLowerCase() === "false") ans = "B";
                    else if (String(ans).toLowerCase() === "a") ans = "A";
                    else if (String(ans).toLowerCase() === "b") ans = "B";
                  } else if (type === "mcq" || type === "quiz") {
                    if (typeof ans === "string") {
                      ans = ans.toUpperCase();
                    }
                  }
                  answersMap[item.sequence || idx + 1] = ans;
                }
              });

              setQuestionsState(reconstructedQuestions);
              setTestAnswers(answersMap);
              setTestMetadata({
                id: resultData.id || pageid,
                name: resultData.name || resultData.title || "Practice Review",
                duration: resultData.duration || 0,
              });

              if (resultData.no_of_question_correct !== undefined) {
                setTestScore(resultData.no_of_question_correct);
              } else if (resultData.score !== undefined) {
                setTestScore(resultData.score);
              }

              isSubmittedRef.current = true;
              setIsTestStarted(true);
              setShowAnswers(true);
              setTimeRemaining(0);
              setLoading(false);
              return;
            }
          }

          response = isInstitution
            ? await getOneQuestionBankInstitution({ id: pageid })
            : await getOneQuestionBank({ id: pageid });

          const responseData = response?.data || response;
          let content = responseData?.content;

          if (typeof content === "string") {
            try {
              content = JSON.parse(content);
            } catch (e) {
              console.error("Error parsing test content:", e);
              content = [];
            }
          }

          if (typeof content === "string") {
            try {
              content = JSON.parse(content);
            } catch (e) {
              console.error("Error parsing test content:", e);
              content = [];
            }
          }

          let questionsArray = [];
          if (Array.isArray(content)) {
            questionsArray = content;
          } else if (content && Array.isArray(content.questions)) {
            questionsArray = content.questions;
          }

          if (questionsArray && questionsArray.length > 0) {
            setTestMetadata({
              id: responseData.id,
              name:
                responseData.name || responseData.title || "Practice Test",
              duration: responseData.duration || 30,
              passPercentage: responseData.pass_percentage,
              course: responseData.course,
              isActive: responseData.is_active,
              pageType: responseData.page_type,
            });

            setTimeDuration(responseData.duration || 30);
            setTimeRemaining((responseData.duration || 30) * 60);
            setQuestionsState(questionsArray);
          } else {
            throw new Error("No questions found in question bank");
          }
          return;
        }

        const courseKey = Cookies.get("courseKey");

        const queryTestPageId = searchParams.get("test_page_content_id");

        if (queryTestPageId) {
          const params = {
            title: searchParams.get("title") || "subtopic",
            key: searchParams.get("key") || courseKey,
            test_page_content_id: queryTestPageId,
          };

          if (isActiveRecall) {
            try {
              response = await getActiveRecallCompletedApi({
                title: params.title,
                key: params.key,
                active_recall_id: params.test_page_content_id,
              });
              const resData = response?.data || response[0]?.data || response[0] || response;
              const completedData = resData?.data || resData;
              if (
                retest === "true" ||
                retest === true ||
                !completedData?.attempt_data ||
                !Array.isArray(completedData.attempt_data) ||
                completedData.attempt_data.length === 0
              ) {
                response = await getTestData(params);
              }
            } catch (err) {
              console.warn("Active recall not completed or failed to fetch completed, loading test questions instead:", err);
              response = await getTestData(params);
            }
          } else {
            response = await getTestData(params);
          }
        } else if (testPageId) {
          const params = {
            title: searchParams.get("title") || "subtopic",
            key: searchParams.get("key") || courseKey,
            test_page_content_id: testPageId,
          };

          if (isActiveRecall) {
            try {
              response = await getActiveRecallCompletedApi({
                title: params.title,
                key: params.key,
                active_recall_id: params.test_page_content_id,
              });
              const resData = response?.data || response[0]?.data || response[0] || response;
              const completedData = resData?.data || resData;
              if (
                retest === "true" ||
                retest === true ||
                !completedData?.attempt_data ||
                !Array.isArray(completedData.attempt_data) ||
                completedData.attempt_data.length === 0
              ) {
                response = await getTestData(params);
              }
            } catch (err) {
              console.warn("Active recall not completed or failed to fetch completed, loading test questions instead:", err);
              response = await getTestData(params);
            }
          } else {
            response = await getTestData(params);
          }
        } else {
          response = await getInitialOnboardingTest(courseKey);
          responseData = response || response?.data;

          if (responseData && responseData.testAvaliabel === false) {
            navigate("/dashboard");
            return;
          }

          if (
            responseData &&
            responseData.testAvaliabel === true &&
            responseData.data &&
            Array.isArray(responseData.data)
          ) {
            setAvailableTests(responseData.data);
            setShowTestSelection(true);
            setLoading(false);
            return;
          }

          if (
            responseData &&
            Array.isArray(responseData.data) &&
            responseData.data.length > 0
          ) {
            responseData = responseData.data[0];
          }
        }

        responseData =
          response?.data || response[0]?.data || response[0] || response;

        if (isActiveRecall && !isReviewParam) {
          const resultData = responseData.data || responseData;
          if (
            resultData?.attempt_data &&
            Array.isArray(resultData.attempt_data)
          ) {
            const reconstructedQuestions = resultData.attempt_data.map(
              (item, idx) => {
                let type = item.type || "mcq";
                if (
                  type === "mcq" &&
                  (!item.options || item.options.length === 0) &&
                  (String(item.answer || "").toLowerCase() === "true" ||
                    String(item.answer || "").toLowerCase() === "false")
                ) {
                  type = "truefalse";
                }

                return {
                  sequence: item.sequence || idx + 1,
                  type: type,
                  content: {
                    question_id: item.sequence || idx + 1,
                    question: item.question,
                    options: normalizeOptions(item.options),
                    pairs: item.pairs || [],
                    answer: item.answer,
                    explanation: item.explanation,
                    hint: item.hint,
                  },
                };
              },
            );

            setQuestionsState(reconstructedQuestions);
            setTestAnswers({});
            setTestMetadata({
              id: resultData.id || testPageId,
              name: resultData.name || resultData.title || "Active Recall Test",
              duration: resultData.duration || TEST_DURATION_MINUTES,
              passPercentage: resultData.pass_percentage,
              course: resultData.course,
              isActive: resultData.is_active,
              pageType: "active_recall_test",
              testType: resultData.test_type,
            });

            const duration = resultData.duration || TEST_DURATION_MINUTES;
            setTimeDuration(duration);
            setTimeRemaining(duration * 60);
            return;
          }
        }

        if (
          isReviewParam &&
          (responseData?.completed || responseData?.data?.completed)
        ) {
          const resultData = responseData.data || responseData;
          if (
            resultData?.attempt_data &&
            Array.isArray(resultData.attempt_data)
          ) {
            const reconstructedQuestions = resultData.attempt_data.map(
              (item, idx) => {
                let type = item.type || "mcq";
                if (
                  type === "mcq" &&
                  (!item.options || item.options.length === 0) &&
                  (String(item.answer || "").toLowerCase() === "true" ||
                    String(item.answer || "").toLowerCase() === "false")
                ) {
                  type = "truefalse";
                }

                return {
                  sequence: item.sequence || idx + 1,
                  type: type,
                  isCorrect: item.isCorrect,
                  content: {
                    question_id: item.sequence || idx + 1,
                    question: item.question,
                    options: item.options,
                    pairs: item.pairs || [],
                    answer: item.answer,
                    explanation: item.explanation,
                    hint: item.hint,
                  },
                };
              },
            );

            const answersMap = {};
            resultData.attempt_data.forEach((item, idx) => {
              let ans = item.user_answer;
              const type = item.type || "mcq";
              if (ans !== undefined && ans !== null) {
                if (type === "trueorfalse" || type === "truefalse") {
                  if (String(ans).toLowerCase() === "true") ans = "A";
                  else if (String(ans).toLowerCase() === "false") ans = "B";
                  else if (String(ans).toLowerCase() === "a") ans = "A";
                  else if (String(ans).toLowerCase() === "b") ans = "B";
                } else if (type === "mcq" || type === "quiz") {
                  if (typeof ans === "string") {
                    ans = ans.toUpperCase();
                  }
                }
                answersMap[item.sequence || idx + 1] = ans;
              }
            });

            setQuestionsState(reconstructedQuestions);
            setTestAnswers(answersMap);
            setTestMetadata({
              id: resultData.id || testPageId,
              name: resultData.name || resultData.title || "Test Review",
              duration: resultData.duration || 30,
            });

            if (resultData.no_of_question_correct !== undefined) {
              setTestScore(resultData.no_of_question_correct);
            }

            isSubmittedRef.current = true;
            setTimeRemaining(0);
            setIsTestStarted(true);
            return;
          }
        }

        const resultDataForReattempt = responseData.data || responseData;
        if (
          !isReviewParam &&
          !retest &&
          resultDataForReattempt?.attempt_data &&
          Array.isArray(resultDataForReattempt.attempt_data)
        ) {
          const reconstructedQuestions =
            resultDataForReattempt.attempt_data.map((item, idx) => {
              let type = "mcq";
              if (
                (!item.options || item.options.length === 0) &&
                (String(item.answer).toLowerCase() === "true" ||
                  String(item.answer).toLowerCase() === "false")
              ) {
                type = "truefalse";
              }

              return {
                sequence: item.sequence || idx + 1,
                type: type,
                content: {
                  question_id: item.sequence || idx + 1,
                  question: item.question,
                  options: item.options,
                  answer: item.answer,
                  explanation: item.explanation,
                  hint: item.hint,
                },
              };
            });

          setQuestionsState(reconstructedQuestions);
          setTestAnswers({});
          setTestMetadata({
            id: resultDataForReattempt.id || testPageId,
            name:
              resultDataForReattempt.name ||
              resultDataForReattempt.title ||
              "Test Reattempt",
            duration: resultDataForReattempt.duration || TEST_DURATION_MINUTES,
            passPercentage: resultDataForReattempt.pass_percentage,
            course: resultDataForReattempt.course,
            isActive: resultDataForReattempt.is_active,
            pageType: resultDataForReattempt.page_type,
            testType: resultDataForReattempt.test_type,
          });

          const duration =
            resultDataForReattempt.duration || TEST_DURATION_MINUTES;
          setTimeDuration(duration);
          setTimeRemaining(duration * 60);
          return;
        }

        let content = responseData?.content || response[0]?.data?.attempt_data;
        if (typeof content === "string") {
          try {
            content = JSON.parse(content);
          } catch (e) {
            console.error("Error parsing test content:", e);
            content = [];
          }
        }

        let questionsArray = [];

        if (Array.isArray(responseData?.questions)) {
          questionsArray = responseData.questions;
        } else if (Array.isArray(content)) {
          questionsArray = content;
        } else if (content && Array.isArray(content.questions)) {
          questionsArray = content.questions;
        }

        if (response && questionsArray && Array.isArray(questionsArray)) {
          setTestMetadata({
            id: responseData.id,
            name: responseData.name || responseData.title || "Test",
            duration: responseData.duration,
            passPercentage: responseData.pass_percentage,
            course: responseData.course,
            isActive: responseData.is_active,
            pageType: responseData.page_type,
            testType: responseData.test_type,
          });

          const duration = responseData.duration || TEST_DURATION_MINUTES;
          setTimeDuration(duration);
          setTimeRemaining(duration * 60);
          setQuestionsState(questionsArray);
        } else {
          throw new Error("Invalid test data format");
        }
      } catch (err) {
        console.error("Error fetching test:", err);
        setError("Failed to load test. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchTestData();
  }, [
    aiTestId,
    homeworkId,
    isPracticeMode,
    isReviewParam,
    navigate,
    pageid,
    searchParams,
    testId,
    testPageId,
  ]);

  useEffect(() => {
    if (isReviewParam && testPageId) {
      const resultKey = `test_result_${testPageId}`;
      const savedResult = Cookies.get(resultKey);
      if (savedResult) {
        try {
          const parsed = JSON.parse(savedResult);

          const resultData = Array.isArray(parsed) ? parsed[0] : parsed;
          const dataContent = resultData?.data || resultData;

          if (dataContent?.answers) {
            setTestAnswers(dataContent.answers);
          } else if (dataContent?.attempt_data) {
            const answersMap = {};
            dataContent.attempt_data.forEach((item, index) => {
              const key = item.sequence || index + 1;
              answersMap[key] = item.user_answer;
            });
            setTestAnswers(answersMap);
          } else if (parsed.answers) {
            setTestAnswers(parsed.answers);
          }

          if (dataContent?.no_of_question_correct !== undefined) {
            setTestScore(dataContent.no_of_question_correct);
          } else if (dataContent?.score !== undefined) {
            setTestScore(dataContent.score);
          } else if (parsed.score !== undefined) {
            setTestScore(parsed.score);
          }

          isSubmittedRef.current = true;
        } catch (e) {
          console.error("Error loading saved result:", e);
        }
      }
    }
  }, [isReviewParam, pageid, searchParams, testPageId]);

  useEffect(() => {
    if (pageid || searchParams.get("test_page_content_id")) return;

    const storedUserRaw = Cookies.get("user");

    if (storedUserRaw) {
      try {
        const parsedUser = JSON.parse(storedUserRaw);
        const testCount = parsedUser?.test_count || 0;
        const iqCompleted = parsedUser?.iqTest_completed === true;
        const psychometricCompleted = parsedUser?.psychometricTest_completed === true;

        let completed = false;
        if (testCount === 2) {
          completed = iqCompleted && psychometricCompleted;
        } else if (testCount === 1) {
          completed = iqCompleted || psychometricCompleted;
        } else if (testCount === 0) {
          completed = true;
        }

        setInitialTestCompleted(completed);

        if (completed === true) {
          if (parsedUser.preference === true) {
            navigate("/dashboard");
          } else {
            navigate("/survey");
          }
        }
      } catch (e) {
        console.error("Error evaluating initial test completion:", e);
      }
    }
  }, [navigate, pageid, searchParams]);

  const handleCompletedClick = async () => {
    try {
      setLoading(true);
      if (key) {
        const payload = {
          subtopic_page_id: testPageId,
          key: keys,
          completed: true,
        };
        const response = await updateCompletedCourseApi(titleName, payload);
        if (response) {
          if (response.data?.page_completion_coins) {
            await updateUserCoins();
          }
          if (response.data?.page_completion_xp) {
            updateUserXP(response.data.page_completion_xp);
          }
          return response;
        }
      }
    } catch (error) {
      console.error("Failed to fetch subjects", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!location?.state?.isAdaptiveTest && true) {
      setTestAnswers({});
      setTestScore(0);
      setIsTestStarted(false);
      setShowResults(false);
      setShowTestComplete(false);
      setShowExitConfirm(false);
      isSubmittedRef.current = false;
      setTimeRemaining(timeDuration * 60 || TEST_DURATION_MINUTES * 60);
      setVisitedQuestions(new Set([0]));
    }
  }, [pageid, searchParams, timeDuration]);

  useEffect(() => {
    setVisitedQuestions((prev) => {
      const newSet = new Set(prev);
      newSet.add(currentQuestionIndex);
      return newSet;
    });
    setShowHint(false);
  }, [currentQuestionIndex]);

  const handleSubmit = useCallback(() => {
    if (isReviewParam || homeworkSubmitted) {
      return;
    }
    setShowSubmitConfirm(true);
  }, [isReviewParam, homeworkSubmitted]);

  useEffect(() => {
    if (loading || questions.length === 0 || !isTestStarted) return;

    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [handleSubmit, isTestStarted, loading, questions]);

  const handleExitConfirm = () => {
    if (isPracticeMode || aiTestId) {
      navigate(-1);
      return;
    }

    if (testPageId && location.state?.isAdaptiveTest) {
      navigate(-1);
    } else if (testPageId) {
      navigate(-1);
    } else {
      navigate("/test");
    }
  };

  const handleExitCancel = async () => {
    try {
      const element = document.documentElement;
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
      }
    } catch (err) {
      console.error("Error re-entering fullscreen:", err);
    } finally {
      setShowExitConfirm(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (
        !document.fullscreenElement &&
        !document.webkitFullscreenElement &&
        !document.msFullscreenElement
      ) {
        if (isTestStarted && !isSubmittedRef.current) {
          setShowExitConfirm(true);
        }
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isTestStarted && !isSubmittedRef.current) {
        e.preventDefault();
        setShowExitConfirm(true);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isTestStarted]);

  useEffect(() => {
    if (!loading && questions.length > 0 && !isTestStarted && !isReviewParam) {
      const requestFullscreen = async () => {
        const element = document.documentElement;
        try {
          if (element.requestFullscreen) {
            await element.requestFullscreen();
          } else if (element.webkitRequestFullscreen) {
            await element.webkitRequestFullscreen();
          } else if (element.msRequestFullscreen) {
            await element.msRequestFullscreen();
          }
          setIsTestStarted(true);
        } catch (e) {
          console.error("Auto fullscreen request failed:", e);
          if (aiTestId) {
            setIsTestStarted(true);
          }
        }
      };

      requestFullscreen();
    }
  }, [loading, questions?.length, isTestStarted, isReviewParam, aiTestId]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${mins
        .toString()
        .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }

    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndexState(currentQuestionIndex + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndexState(currentQuestionIndex - 1);
    }
  };

  const getQuestionId = (q) => {
    let content = q.content || q;
    try {
      if (typeof content === "string") content = JSON.parse(content);
      if (typeof content === "string") content = JSON.parse(content);
    } catch (e) {}

    const type = (q.type || "").toLowerCase().trim();
    const questionObj =
      type === "match" ? content : content?.questions?.[0] || content;

    return (
      content?.question_id ||
      content?.id ||
      q.question_id ||
      q.id ||
      q._id ||
      q.sequence ||
      questionObj?.question_id ||
      questionObj?.id ||
      questionObj?.question
    );
  };

  useEffect(() => {
    const isRetestMode = searchParams.get("retest") === "true";
    if (isRetestMode && questions && questions.length > 0) {
      setTestAnswers((prevAnswers) => {
        const initialAnswers = { ...prevAnswers };
        let updated = false;

        questions.forEach((q) => {
          const type = (q.type || "").toLowerCase().trim();
          if (type === "match") {
            const questionId = getQuestionId(q);
            if (!initialAnswers[questionId]) {
              let content = q.content || q;
              try {
                if (typeof content === "string") {
                  content = JSON.parse(content);
                }
                if (typeof content === "string") {
                  content = JSON.parse(content);
                }
              } catch (e) {}

              let pairs = [];
              if (Array.isArray(content?.pairs)) {
                pairs = content.pairs;
              } else if (content?.content) {
                let parsedContent = content.content;
                if (typeof parsedContent === "string") {
                  try {
                    parsedContent = JSON.parse(parsedContent);
                  } catch (e) {}
                }
                if (Array.isArray(parsedContent?.pairs)) {
                  pairs = parsedContent.pairs;
                } else {
                  pairs = parsedContent?.questions || [];
                }
              } else if (content?.questions) {
                pairs = content.questions;
              } else if (Array.isArray(content)) {
                pairs = content;
              }

              const filteredPairs = pairs.filter(
                (item) =>
                  item && typeof item.key === "string" && Array.isArray(item.values),
              );

              if (filteredPairs.length >= 2) {
                const leftCol = filteredPairs[0];
                const rightCol = filteredPairs[1];

                let shuffledArray = [...rightCol.values];
                let isSame = true;
                let attempts = 0;

                while (isSame && attempts < 10) {
                  for (let i = shuffledArray.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [shuffledArray[i], shuffledArray[j]] = [
                      shuffledArray[j],
                      shuffledArray[i],
                    ];
                  }

                  isSame = shuffledArray.every(
                    (val, index) => val === rightCol.values[index],
                  );
                  attempts++;
                }

                if (isSame && shuffledArray.length > 1) {
                  const temp = shuffledArray[0];
                  shuffledArray[0] = shuffledArray[1];
                  shuffledArray[1] = temp;
                }

                const shuffledItems = [
                  leftCol,
                  { key: rightCol.key, values: shuffledArray },
                ];

                initialAnswers[questionId] = {
                  value: shuffledItems,
                  isSubmitted: false,
                };
                updated = true;
              }
            }
          }
        });

        return updated ? initialAnswers : prevAnswers;
      });
    }
  }, [questions, searchParams]);

  const calculateScore = () => {
    let calculatedScore = 0;
    questions?.forEach((q) => {
      let content = q.content || q;
      try {
        if (typeof content === "string") content = JSON.parse(content);
        if (typeof content === "string") content = JSON.parse(content);
      } catch (e) {
        return;
      }

      const type = (q.type || "").toLowerCase().trim();

      const questionObj =
        type === "match" ? content : content?.questions?.[0] || content;
      if (!questionObj) return;

      const questionId = getQuestionId(q);

      const userAnswerRaw = testAnswers[questionId];

      if (!userAnswerRaw) return;

      const userAnswer =
        userAnswerRaw &&
        typeof userAnswerRaw === "object" &&
        "value" in userAnswerRaw
          ? userAnswerRaw.value
          : userAnswerRaw;

      if (
        type === "mcq" ||
        type === "quiz" ||
        type === "trueorfalse" ||
        type === "truefalse"
      ) {
        const correct = questionObj.correct_answer || questionObj.answer;

        let normUser = String(userAnswer).trim().toLowerCase();
        let normCorrect = String(correct).trim().toLowerCase();

        if (type === "trueorfalse" || type === "truefalse") {
          if (normUser === "true") normUser = "a";
          else if (normUser === "false") normUser = "b";

          if (normCorrect === "true") normCorrect = "a";
          else if (normCorrect === "false") normCorrect = "b";
        }

        if (normUser === normCorrect) calculatedScore++;
      } else if (type === "fillups") {
        const val = userAnswer;
        const correct = questionObj.answer;
        if (
          val &&
          String(val).trim().toLowerCase() ===
            String(correct).trim().toLowerCase()
        ) {
          calculatedScore++;
        }
      } else if (type === "match") {
        const userPairs = userAnswer;
        const correctPairs = questionObj.pairs || questionObj.questions || [];
        if (
          Array.isArray(userPairs) &&
          userPairs.length >= 2 &&
          Array.isArray(correctPairs) &&
          correctPairs.length >= 2
        ) {
          const userRightCol = userPairs[1].values;
          const correctRightCol = correctPairs[1].values;
          if (
            JSON.stringify(userRightCol) === JSON.stringify(correctRightCol)
          ) {
            calculatedScore++;
          }
        }
      }
    });
    return calculatedScore;
  };

  const areAllQuestionsAnswered = () => {
    if (!questions || questions.length === 0) return false;

    let answeredCount = 0;

    questions.forEach((q) => {
      let content = q.content || q;
      try {
        if (typeof content === "string") content = JSON.parse(content);
        if (typeof content === "string") content = JSON.parse(content);
      } catch (e) {
        return;
      }

      const type = (q.type || "").toLowerCase().trim();

      const questionObj =
        type === "match" ? content : content?.questions?.[0] || content;
      if (!questionObj) return;

      const questionId = getQuestionId(q);

      const userAnswer = testAnswers[questionId];

      if (userAnswer !== undefined && userAnswer !== null) {
        answeredCount += 1;
      }
    });

    return answeredCount === questions.length;
  };

  const buildInitialOnboardingPayload = (score) => {
    if (!testMetadata || !Array.isArray(questions)) return null;

    const initial_onboarding_test = testMetadata.id;

    const attempt_data = [];
    const categoryStats = {};

    questions.forEach((q, index) => {
      let content = q.content || q;
      try {
        if (typeof content === "string") content = JSON.parse(content);
        if (typeof content === "string") content = JSON.parse(content);
      } catch (e) {
        return;
      }

      const type = (q.type || "").toLowerCase().trim();

      let questionObj =
        type === "match" ? content : content?.questions?.[0] || content;
      if (!questionObj) return;

      const question_id =
        questionObj.id ||
        questionObj.question_id ||
        questionObj.question ||
        q.sequence ||
        index + 1;

      const categoryKey = (
        q.categories ||
        questionObj.type ||
        "unknown"
      ).toString();

      const questionKey = getQuestionId(q);

      const userAnswerRaw = testAnswers[questionKey];
      if (userAnswerRaw === undefined || userAnswerRaw === null) {
        return;
      }

      let correctAnswer = questionObj.correct_answer || questionObj.answer;
      let user_answer =
        userAnswerRaw &&
        typeof userAnswerRaw === "object" &&
        "value" in userAnswerRaw
          ? userAnswerRaw.value
          : userAnswerRaw;
      let is_correct = false;

      if (
        type === "mcq" ||
        type === "quiz" ||
        type === "trueorfalse" ||
        type === "truefalse"
      ) {
        let normUser = String(user_answer).trim().toLowerCase();
        let normCorrect = String(correctAnswer).trim().toLowerCase();

        if (type === "trueorfalse" || type === "truefalse") {
          if (normUser === "true") normUser = "a";
          else if (normUser === "false") normUser = "b";

          if (normCorrect === "true") normCorrect = "a";
          else if (normCorrect === "false") normCorrect = "b";
        }

        is_correct = normUser === normCorrect;
      } else if (type === "fillups") {
        const val =
          userAnswerRaw &&
          typeof userAnswerRaw === "object" &&
          "value" in userAnswerRaw
            ? userAnswerRaw.value
            : userAnswerRaw;
        user_answer = val;
        const normUser = val ? String(val).trim().toLowerCase() : "";
        const normCorrect = String(correctAnswer || "")
          .trim()
          .toLowerCase();
        is_correct = normUser !== "" && normUser === normCorrect;
      } else if (type === "match") {
        const val =
          userAnswerRaw &&
          typeof userAnswerRaw === "object" &&
          "value" in userAnswerRaw
            ? userAnswerRaw.value
            : userAnswerRaw;
        user_answer = val;
        const correctPairs = questionObj.pairs || questionObj.questions || [];
        if (
          Array.isArray(val) &&
          val.length >= 2 &&
          Array.isArray(correctPairs) &&
          correctPairs.length >= 2
        ) {
          is_correct =
            JSON.stringify(val[1].values) ===
            JSON.stringify(correctPairs[1].values);
        }
      }

      if (!categoryStats[categoryKey]) {
        categoryStats[categoryKey] = { correct: 0, total: 0 };
      }
      categoryStats[categoryKey].total += 1;
      if (is_correct) {
        categoryStats[categoryKey].correct += 1;
      }

      attempt_data.push({
        sequence: q.sequence || index + 1,
        category: categoryKey,
        type,
        question_id,
        question_data: {
          id: question_id,
          type: questionObj.type || categoryKey,
          question: questionObj.question,
          options: questionObj.options,
          pairs: questionObj.pairs,
          correct_answer: correctAnswer,
          hint: questionObj.hint || content?.hint,
        },
        user_answer,
        isCorrect: is_correct,
        categories: q.categories || questionObj.type || "",
        pairs:
          type === "match"
            ? questionObj.pairs || questionObj.questions || []
            : undefined,
      });
    });

    const attempt_questions = attempt_data.length;

    const category_scores = Object.keys(categoryStats).reduce((acc, key) => {
      const { correct, total } = categoryStats[key];
      if (total > 0) {
        acc[key] = Math.round((correct / total) * 100);
      }
      return acc;
    }, {});

    const percentage =
      questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;

    return {
      initial_onboarding_test,
      attempt_questions,
      score: percentage,
      completed: true,
      category_scores,
      attempt_data,
    };
  };

  const buildTopicTestPayload = (score) => {
    if (!Array.isArray(questions)) return null;

    const attempt_data = [];
    let no_of_questions_correct = 0;

    questions.forEach((q) => {
      let content = q.content || q;
      try {
        if (typeof content === "string") content = JSON.parse(content);
        if (typeof content === "string") content = JSON.parse(content);
      } catch (e) {
        return;
      }

      const type = (q.type || "").toLowerCase().trim();

      const questionObj =
        type === "match" ? content : content?.questions?.[0] || content;
      if (!questionObj) return;

      const questionKey = getQuestionId(q);

      const userAnswerRaw = testAnswers[questionKey];
      if (userAnswerRaw === undefined || userAnswerRaw === null) {
        return;
      }

      let correctAnswer =
        questionObj.correct_answer || questionObj.answer || "";
      let user_answer =
        userAnswerRaw &&
        typeof userAnswerRaw === "object" &&
        "value" in userAnswerRaw
          ? userAnswerRaw.value
          : userAnswerRaw;
      let is_correct = false;

      if (
        type === "mcq" ||
        type === "quiz" ||
        type === "trueorfalse" ||
        type === "truefalse"
      ) {
        let normUser = String(user_answer).trim().toLowerCase();
        let normCorrect = String(correctAnswer).trim().toLowerCase();

        if (type === "trueorfalse" || type === "truefalse") {
          if (normUser === "true") normUser = "a";
          else if (normUser === "false") normUser = "b";

          if (normCorrect === "true") normCorrect = "a";
          else if (normCorrect === "false") normCorrect = "b";
        }

        is_correct = normUser === normCorrect;
        user_answer = normUser;
      } else if (type === "fillups") {
        const val =
          userAnswerRaw &&
          typeof userAnswerRaw === "object" &&
          "value" in userAnswerRaw
            ? userAnswerRaw.value
            : userAnswerRaw;
        user_answer = val;
        const normUser = val ? String(val).trim().toLowerCase() : "";
        const normCorrect = String(correctAnswer || "")
          .trim()
          .toLowerCase();
        is_correct = normUser !== "" && normUser === normCorrect;
      } else if (type === "match") {
        const val =
          userAnswerRaw &&
          typeof userAnswerRaw === "object" &&
          "value" in userAnswerRaw
            ? userAnswerRaw.value
            : userAnswerRaw;
        user_answer = val;
        const correctPairs = questionObj.pairs || questionObj.questions || [];
        if (
          Array.isArray(val) &&
          val.length >= 2 &&
          Array.isArray(correctPairs) &&
          correctPairs.length >= 2
        ) {
          is_correct =
            JSON.stringify(val[1].values) ===
            JSON.stringify(correctPairs[1].values);
        }
      }

      if (is_correct) {
        no_of_questions_correct += 1;
      }

      attempt_data.push({
        question: questionObj.question || "",
        options: questionObj.options || [],
        type,
        answer: correctAnswer,
        explanation: questionObj.explanation || "",
        user_answer,
        isCorrect: is_correct,
        categories: q.categories || questionObj.type || "",
        pairs:
          type === "match"
            ? questionObj.pairs || questionObj.questions || []
            : undefined,
      });
    });

    const no_of_questions_attempted = attempt_data.length;
    const total_questions = questions.length;

    return {
      attempt_data,
      no_of_questions_attempted,
      no_of_question_correct: no_of_questions_correct,
      total_questions,
    };
  };

  const buildHomeworkPayload = (score) => {
    const topicPayload = buildTopicTestPayload(score);
    if (!topicPayload) return null;

    const { attempt_data, no_of_questions_attempted, no_of_question_correct } =
      topicPayload;

    return {
      attempt_data,
      completed: true,
      no_of_questions_attempted,
      no_of_question_correct,
    };
  };

  const buildAdaptivePayload = (score) => {
    if (!Array.isArray(questions)) return null;

    const attempt_data = [];

    let no_of_questions_correct = 0;

    questions.forEach((q) => {
      let content = q.content || q;
      try {
        if (typeof content === "string") content = JSON.parse(content);
        if (typeof content === "string") content = JSON.parse(content);
      } catch (e) {
        return;
      }

      const type = (q.type || "").toLowerCase().trim();

      const questionObj =
        type === "match" ? content : content?.questions?.[0] || content;
      if (!questionObj) return;

      const questionKey = getQuestionId(q);

      const userAnswerRaw = testAnswers[questionKey];
      if (userAnswerRaw === undefined || userAnswerRaw === null) {
        return;
      }

      let correctAnswer =
        questionObj.correct_answer || questionObj.answer || "";
      let user_answer = userAnswerRaw;
      let is_correct = false;

      if (
        type === "mcq" ||
        type === "quiz" ||
        type === "trueorfalse" ||
        type === "truefalse"
      ) {
        let normUser = String(userAnswerRaw).trim().toLowerCase();
        let normCorrect = String(correctAnswer).trim().toLowerCase();

        if (type === "trueorfalse" || type === "truefalse") {
          if (normUser === "true") normUser = "a";
          else if (normUser === "false") normUser = "b";

          if (normCorrect === "true") normCorrect = "a";
          else if (normCorrect === "false") normCorrect = "b";
        }

        is_correct = normUser === normCorrect;
        user_answer = normUser;
      } else if (type === "fillups") {
        const val =
          userAnswerRaw &&
          typeof userAnswerRaw === "object" &&
          "value" in userAnswerRaw
            ? userAnswerRaw.value
            : userAnswerRaw;
        user_answer = val;
        const normUser = val ? String(val).trim().toLowerCase() : "";
        const normCorrect = String(correctAnswer || "")
          .trim()
          .toLowerCase();
        is_correct = normUser !== "" && normUser === normCorrect;
      }

      if (is_correct) {
        no_of_questions_correct += 1;
      }

      const options = questionObj.options;

      attempt_data.push({
        question: questionObj.question || "",
        options,
        type,
        answer: correctAnswer,
        explanation: questionObj.explanation || "",
        user_answer,
        isCorrect: is_correct,
        categories: q.categories || questionObj.type || "",
        pairs:
          type === "match"
            ? questionObj.pairs || questionObj.questions || []
            : undefined,
      });
    });
    return {
      attempt_data,
      no_of_questions_attempted: attempt_data.length,
      no_of_question_correct: no_of_questions_correct,
      total_questions: questions.length,
    };
  };

  const handleSubmitConfirmed = async () => {
    setShowSubmitConfirm(false);
    try {
      if (
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      ) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }

      isSubmittedRef.current = true;

      const score = calculateScore();

      if (location.state?.isAdaptiveTest && testPageId) {
        try {
          setLoading(true);

          const score = calculateScore();
          const adaptivePayload = buildAdaptivePayload(score);

          if (!adaptivePayload) {
            navigate(-1);
          }

          const res = await submitAdaptiveLearningTest(
            testPageId,
            adaptivePayload,
          );

          navigate(-1);

          return;
        } catch (e) {
          console.error("Adaptive test submit failed:", e);
        } finally {
          setLoading(false);
        }
      }

      if (aiTestId) {
        const score = calculateScore();
        const percentage =
          questions.length > 0
            ? Math.round((score / questions.length) * 100)
            : 0;

        const aiTestPayload = {
          id: testMetadata?.questionBankId,
          mark: percentage,
          level: testMetadata?.difficulty || "medium",
        };

        try {
          setLoading(true);
          const res = await submitQuestionBankComplete(aiTestPayload);
          if (res) {
            if (res.page_completion_coins) {
              await updateUserCoins();
            }
            if (res.page_completion_xp) {
              updateUserXP(res.page_completion_xp);
            }
          }
        } catch (e) {
          console.error("Failed to submit AI test results:", e);
        } finally {
          setLoading(false);
        }
        navigate(-1);
        return;
      }
      if (isActiveRecall) {
        const resultPayload = buildTopicTestPayload(score);
        try {
          setLoading(true);
          const params = {
            title: titleName || "subtopic",
            key: keys,
            active_recall_id: testPageId,
            parent_id: parentId,
          };
          await submitActiveRecallCompleted(params, resultPayload);
          setLoading(false);
        } catch (e) {
          console.error("Failed to submit active recall results:", e);
        } finally {
          setLoading(false);
        }
        navigate(-1);
        return;
      }

      if (homeworkId) {
        const homeworkPayload = buildHomeworkPayload(score);
        if (!homeworkPayload) {
          navigate(-1);
          return;
        }

        try {
          setLoading(true);
          await submitHomework(homeworkId, homeworkLevel, homeworkPayload);
        } catch (e) {
          console.error("Failed to submit homework:", e);
        } finally {
          setLoading(false);
        }

        navigate(-1);
        return;
      }

      if (testPageId && !isPracticeMode) {
        const resultPayload = buildTopicTestPayload(score);
        if (!resultPayload) {
          navigate(-1);
          return;
        }

        const courseKeyFromStorage = Cookies.get("courseKey");

        const meta = {
          testPageId,
          title: searchParams.get("title") || "subtopic",
          key: searchParams.get("key") || courseKeyFromStorage,
        };

        try {
          setLoading(true);
          if (retest) {
            const res = await reattemptTest(resultPayload, meta);
            if (res) {
              const resultArray = Array.isArray(res) ? res : [res];
              const first = resultArray[0] || {};
              const gainedCoins =
                typeof first.test_completion_coins === "number"
                  ? first.test_completion_coins
                  : first.page_completion_coins;
              const gainedXp =
                typeof first.test_completion_xp === "number"
                  ? first.test_completion_xp
                  : first.page_completion_xp;
              if (typeof gainedCoins === "number") {
                await updateUserCoins();
              }
              if (typeof gainedXp === "number") {
                updateUserXP(gainedXp);
              }
              handleCompletedClick();
              setLoading(false);
            }
          } else {
            const res = await submitTestResults(resultPayload, meta);
            if (res) {
              const resultArray = Array.isArray(res) ? res : [res];
              const first = resultArray[0] || {};

              const gainedCoins =
                typeof first.test_completion_coins === "number"
                  ? first.test_completion_coins
                  : first.page_completion_coins;
              const gainedXp =
                typeof first.test_completion_xp === "number"
                  ? first.test_completion_xp
                  : first.page_completion_xp;

              if (typeof gainedCoins === "number") {
                await updateUserCoins();
              }
              if (typeof gainedXp === "number") {
                updateUserXP(gainedXp);
              }

              handleCompletedClick();
              setLoading(false);
            }
          }
        } catch (e) {
          setLoading(false);
          console.error("Failed to submit test results:", e);
        } finally {
          setLoading(false);
        }

        Cookies.set(`test_result_${testPageId}`, JSON.stringify(resultPayload));

        navigate(-1);

        return;
      }

      setTestScore(score);

      if (isPracticeMode) {
        const resultPayload = buildTopicTestPayload(score);
        const folder_id =
          searchParams.get("parent_id") ||
          location.state?.questionBank?.questionbank_folders ||
          location.state?.questionBank?.questionbank_folder;
        const isInstitution = searchParams.get("type") === "institution";

        try {
          setLoading(true);
          if (isInstitution) {
            await submitQuestionBankInstitutionResult(
              {
                questionbank_folders: folder_id,
                question_bank: pageid,
              },
              resultPayload,
            );
          } else {
            await submitQuestionBankResult(
              {
                questionbank_folders: folder_id,
                question_bank: pageid,
              },
              resultPayload,
            );
          }
        } catch (e) {
          console.error("Failed to submit practice test results:", e);
        } finally {
          setLoading(false);
        }
        navigate(`/question-bank-folder/${folder_id}/${pageid}${isInstitution ? "?type=institution" : ""}`);
        return;
      } else {
        if (isAdmin) {
          navigate(-1);
          return;
        }
        setShowResults(true);
        setShowTestComplete(true);
      }

      if (!isPracticeMode) {
        if (!testPageId) {
          const payload = buildInitialOnboardingPayload(score);
          if (payload) {
            try {
              setLoading(true);
              const res = await submitInitialResults(payload);
              if (res) {
                if (res.page_completion_coins) {
                  await updateUserCoins();
                }
                if (res.page_completion_xp) {
                  updateUserXP(res.page_completion_xp);
                }
                setLoading(false);
              }
            } catch (e) {
              setLoading(false);
              console.error(
                "Failed to submit initial onboarding test results:",
                e,
              );
            }
          }
        }

        setTimeout(() => {
          if (!testPageId || !aiTestId) {
            const updateUserTestStatus = () => {
              const storedUser = Cookies.get("user");
              if (storedUser) {
                try {
                  const parsed = JSON.parse(storedUser);
                  const type = testMetadata?.testType;

                  if (type === "iq") {
                    parsed.iqTest_completed = true;
                  } else if (type === "psychometric") {
                    parsed.psychometricTest_completed = true;
                  }

                  Cookies.set("user", JSON.stringify(parsed));
                } catch (e) {
                  console.error("Error updating test status:", e);
                }
              }
            };

            updateUserTestStatus();

            const storedUser = Cookies.get("user");
            if (storedUser) {
              try {
                const parsed = JSON.parse(storedUser);
                const testCount = parsed?.test_count || 0;
                const iqCompleted = parsed?.iqTest_completed === true;
                const psychometricCompleted =
                  parsed?.psychometricTest_completed === true;

                if (
                  testCount === 2 &&
                  (!iqCompleted || !psychometricCompleted)
                ) {
                  navigate("/test");
                  return;
                }
                if (testCount === 1 && !iqCompleted && !psychometricCompleted) {
                  navigate("/test");
                  return;
                }
                if (parsed.preference === true) {
                  navigate("/dashboard");
                } else {
                  navigate("/survey");
                }
              } catch (e) {
                console.error("Error checking test completion status:", e);
                navigate("/dashboard");
              }
            } else {
              navigate("/dashboard");
            }
          }
        }, 1000);
      }
    } catch (err) {
      console.error("Error during test submission:", err);
      isSubmittedRef.current = true;
      const score = calculateScore();
      setTestScore(score);
      if (isAdmin) {
        navigate(-1);
        return;
      }
      setShowResults(true);
      setShowTestComplete(true);

      setTimeout(() => {
        if (!pageid && !isPracticeMode) {
          const updateUserTestStatus = () => {
            const storedUser = Cookies.get("user");
            if (storedUser) {
              try {
                const parsed = JSON.parse(storedUser);
                const type = testMetadata?.testType;

                if (type === "iq") {
                  parsed.iqTest_completed = true;
                } else if (type === "psychometric") {
                  parsed.psychometricTest_completed = true;
                }

                Cookies.set("user", JSON.stringify(parsed));
              } catch (e) {
                console.error("Error updating test status:", e);
              }
            }
          };

          updateUserTestStatus();

          const storedUser = Cookies.get("user");
          if (storedUser) {
            try {
              const parsed = JSON.parse(storedUser);
              const testCount = parsed?.test_count || 0;
              const iqCompleted = parsed?.iqTest_completed === true;
              const psychometricCompleted =
                parsed?.psychometricTest_completed === true;

              if (testCount === 2 && (!iqCompleted || !psychometricCompleted)) {
                navigate("/test");
                return;
              }
              if (testCount === 1 && !iqCompleted && !psychometricCompleted) {
                navigate("/test");
                return;
              }
              if (parsed.preference === true) {
                navigate("/dashboard");
              } else {
                navigate("/survey");
              }
            } catch (e) {
              console.error("Error checking test completion status:", e);
              navigate("/dashboard");
            }
          } else {
            navigate("/dashboard");
          }
        }
      }, 1000);
    }
  };

  const handleTestComplete = () => {
    setShowTestComplete(false);

    if (testPageId || isPracticeMode || aiTestId) {
      navigate(-1);
    } else {
      const storedUser = Cookies.get("user");
      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          const testCount = parsed?.test_count || 0;
          const iqCompleted = parsed?.iqTest_completed === true;
          const psychometricCompleted =
            parsed?.psychometricTest_completed === true;

          if (testCount === 2 && (!iqCompleted || !psychometricCompleted)) {
            navigate("/test");
            return;
          }
          if (testCount === 1 && !iqCompleted && !psychometricCompleted) {
            navigate("/test");
            return;
          }
          if (parsed.preference === true) {
            navigate("/dashboard");
          } else {
            navigate("/survey");
          }
        } catch (e) {
          console.error("Error checking test completion status:", e);
          navigate("/dashboard");
        }
      } else {
        navigate("/dashboard");
      }
    }
  };

  const getQuestionHeading = (type) => {
    const map = {
      mcq: "Multiple Choice Questions",
      trueorfalse: "True or False",
      match: "Match the Following",
      analysis: "Conversational Analysis",
      fillups: "Fill in the Blanks",
    };

    return map[type?.toLowerCase()] || "Questions";
  };

  const getCategoryLabel = (category) => {
    if (!category) return "";

    const map = {
      application: "Application",
      reasoning: "Reasoning",
      direct: "Direct",
      critical_thinking: "Critical Thinking",
    };

    const key = category.toLowerCase();
    if (map[key]) return map[key];

    const withoutUnderscore = category.replace(/_/g, " ");
    return withoutUnderscore
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      application: "/assets/images/applicationImg.svg",
      direct: "/assets/images/directImg.svg",
      reasoning: "/assets/images/reasoningImg.svg",
      critical_thinking: "/assets/images/criticalThinkingImg.svg",
    };

    const iconSrc = iconMap[category?.toLowerCase()];
    if (iconSrc) {
      return <img src={iconSrc} alt={category} className="category-icon" />;
    } else {
      return (
        <img
          src={"/assets/images/defaultBrainIcon.svg"}
          alt={category}
          className="category-icon"
        />
      );
    }
  };

  const renderCurrentQuestion = () => {
    const questionData = questions[currentQuestionIndex];
    if (!questionData) return null;

    let content = questionData.content || questionData;
    try {
      if (typeof content === "string") {
        content = JSON.parse(content);
      }
      if (typeof content === "string") {
        content = JSON.parse(content);
      }
    } catch (e) {
      console.error("Error parsing content:", e);
    }

    if (content && Array.isArray(content.questions)) {
      const explanationItemIndex = content.questions.findIndex(
        (q) => q.explanation && !q.question && !q.key,
      );
      if (explanationItemIndex !== -1) {
        content.explanation =
          content.questions[explanationItemIndex].explanation;
        content.questions = content.questions.filter(
          (_, i) => i !== explanationItemIndex,
        );
      }
    }

    const type = (questionData.type || "").toLowerCase();
    const category = questionData.categories;
    const hint = (content && content.hint) || questionData.hint;

    if (questionData.sequence) {
      if (Array.isArray(content.questions)) {
        content.questions = content.questions.map((q) => ({
          ...q,
          question_id: q.question_id || questionData.sequence,
        }));
      }
    }

    const questionIdForComponent = getQuestionId(questionData);

    const isReviewMode = Boolean(pageid) && isSubmittedRef.current;

    return (
      <div className="question-container">
        <div className="title-wrapper">
          {type && (
            <div className="main-title">
              <span className="title-icon">
                <PsychologyIcon />
              </span>
              {getQuestionHeading(type)}
            </div>
          )}

          {(category || hint) && (
            <div className="question-category">
              {hint && (
                <button
                  className="hint-button"
                  onClick={() => setShowHint(!showHint)}
                  title="Show Hint"
                >
                  <LightbulbIcon />
                  <strong className="desktop-item">Hint</strong>
                </button>
              )}
              {category && (
                <span
                  className={`category-badge ${category
                    .toLowerCase()
                    .replaceAll("_", "")}`}
                >
                  {getCategoryIcon(category)}
                  {getCategoryLabel(category)}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="question-content custom-scrollbar">
          {(() => {
            switch (type) {
              case "match":
                return (
                  <Match
                    key={questionIdForComponent}
                    data={content}
                    mode={isReviewMode ? "review" : "test"}
                    answers={testAnswers}
                    setAnswers={setTestAnswers}
                    questionId={questionIdForComponent}
                    retest={retest === "true" || retest === true}
                    isAttended={questionData?.attended || content?.attended}
                  />
                );
              case "trueorfalse":
              case "truefalse":
                return (
                  <TrueFalse
                    key={questionIdForComponent}
                    questions={content?.questions || [content]}
                    answers={testAnswers}
                    setAnswers={setTestAnswers}
                    mode={isReviewMode ? "review" : "test"}
                    questionId={questionIdForComponent}
                  />
                );
              case "mcq":
              case "quiz":
                return (
                  <MCQ
                    key={questionIdForComponent}
                    questions={content?.questions || [content]}
                    current={0}
                    answers={testAnswers}
                    setAnswers={setTestAnswers}
                    mode={isReviewMode ? "review" : "test"}
                    questionId={questionIdForComponent}
                  />
                );
              case "fillups":
                return (
                  <Fillups
                    key={questionIdForComponent}
                    questions={content?.questions || [content]}
                    current={0}
                    answers={testAnswers}
                    setAnswers={setTestAnswers}
                    mode={isReviewMode ? "review" : "test"}
                    questionId={questionIdForComponent}
                  />
                );
              case "analysis":
              case "conversation":
                const questionText =
                  content?.questions?.[0]?.question ||
                  content?.question ||
                  "Analyze this topic.";

                return (
                  <ConversationalAnalysis
                    key={questionData.sequence || currentQuestionIndex}
                    question={questionText}
                    onNext={handleNext}
                  />
                );
              default:
                return <div>Unknown question type: {type}</div>;
            }
          })()}
          {showHint && hint && (
            <div className="hint-content">
              <strong>Hint:</strong>
              {hint}
            </div>
          )}
        </div>
      </div>
    );
  };

  const showFooter = questions?.length > 0 && true;

  const isTimeRunningOut = timeRemaining <= 300;

  if (!isTestStarted && !loading && !error && !testPageId && !showResults) {
    if (initialTestCompleted === true) {
      const storedUser = Cookies.get("user");
      let pref = false;
      if (storedUser) {
        try {
          pref = JSON.parse(storedUser)?.preference === true;
        } catch (e) {}
      }
      if (pref) {
        navigate("/dashboard");
      } else {
        navigate("/survey");
      }
      return null;
    }

    if (showTestSelection && availableTests.length > 0) {
      return <AvailableTestCards tests={availableTests} />;
    }

    return (
      <div className="test-page-container">
        <AvailableTestCards tests={availableTests} />
      </div>
    );
  }

  return (
    <>
      {loading && <GlobalLoader />}
      <div className="test-page-container">
        <div className="test-header">
          <div className="test-info">
            <h2>{testMetadata?.name || "Initial Onboarding Test"}</h2>
            <span className={`timer ${isTimeRunningOut ? "warning" : ""}`}>
              ⏱ {formatTime(timeRemaining)}
            </span>
          </div>
          <div className="btns-wrapper">
            <div className="question-tracker">
              Question {currentQuestionIndex + 1} of {questions.length}
            </div>
            {(() => {
              const hasAnyAnswer = Object.keys(testAnswers).length > 0;
              const canSubmit =
                isPracticeMode ? hasAnyAnswer : areAllQuestionsAnswered();

              if (isReviewParam || homeworkSubmitted) {
                return (
                  <button
                    className="btn-secondary"
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </button>
                );
              }

              return (
                <button
                  className="btn-submit"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                >
                  Submit Test
                </button>
              );
            })()}
          </div>
        </div>

        <div className="test-body">
          {questions?.length > 0 ? (
            <div className="question-wrapper custom-scrollbar">
              {renderCurrentQuestion()}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <img
                src={notAvailable}
                alt="PowerPoint Icon"
                style={{ width: "300px", height: "300px", marginTop: "4px" }}
              />
              <h3>No Questions Found!</h3>
            </div>
          )}
          {questions?.length > 0 && (
            <QuestionPalette
              questions={questions}
              currentQuestionIndex={currentQuestionIndex}
              setCurrentQuestionIndex={setCurrentQuestionIndexState}
              testAnswers={testAnswers}
              visitedQuestions={visitedQuestions}
            />
          )}
        </div>

        {showFooter && (
          <div className="test-footer">
            <button
              className="btn-secondary"
              onClick={handlePrev}
              disabled={currentQuestionIndex === 0}
            >
              Previous
            </button>
            <div className="btns-wrapper">
              {currentQuestionIndex === questions.length - 1 &&
              areAllQuestionsAnswered() &&
              !(isReviewParam || homeworkSubmitted) ? (
                <button
                  className="btn-submit"
                  onClick={handleSubmit}
                >
                  Submit Test
                </button>
              ) : (
                <button
                  className={`btn-primary ${
                    currentQuestionIndex === questions.length - 1
                      ? "disabled"
                      : ""
                  }`}
                  onClick={handleNext}
                  disabled={currentQuestionIndex === questions.length - 1}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        )}
        {showExitConfirm && (
          <ConfirmationPopup
            open={showExitConfirm}
            onClose={handleExitCancel}
            onConfirm={handleExitConfirm}
            title="Leave Test?"
            message={messageText}
            confirmText="Leave Test"
            variant="warning"
          />
        )}

        {showSubmitConfirm && (
          <ConfirmationPopup
            open={showSubmitConfirm}
            onClose={() => setShowSubmitConfirm(false)}
            onConfirm={handleSubmitConfirmed}
            title="Submit Test?"
            message="Are you sure you want to submit the test? You won't be able to change your answers after submission."
            confirmText="Submit"
            cancelText="Cancel"
            variant="success"
          />
        )}

        {showTestComplete && (
          <ConfirmationPopup
            open={showTestComplete}
            onClose={handleTestComplete}
            onConfirm={handleTestComplete}
            button={false}
            title="Test Submitted Successfully!"
            message="You will be redirected to the dashboard."
            confirmText="OK"
            variant="success"
          />
        )}

        {showPracticeResults === true && (
          <div className="practice-test-results">
            <div className="practice-results-header">
              <h2>Practice Test Completed!</h2>
              <div className="score-display">
                <div className="score-circle">
                  <span className="score-number">{testScore}</span>
                  <span className="score-total">/ {questions.length}</span>
                </div>
                <div className="score-percentage">
                  {Math.round((testScore / questions.length) * 100)}%
                </div>
              </div>
            </div>

            <div className="practice-results-actions">
              <button
                className="btn-primary show-answers-btn"
                onClick={() => setShowAnswers(true)}
              >
                Show Answers
              </button>
              <button
                className="btn-secondary back-btn"
                onClick={() => navigate(-1)}
              >
                Back to Previous
              </button>
            </div>

            {showAnswers && (
              <div className="answers-section">
                <div className="answers-header">
                  <h3>Answer Key</h3>
                  <button
                    className="btn-close"
                    onClick={() => setShowAnswers(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="answers-list">
                  {questions.map((questionItem, index) => {
                    let questionText = "";
                    let correctAnswer = "";
                    let userAnswer = "";
                    let explanation = "";
                    let isCorrect = false;
                    if (!questionItem.content && questionItem.question) {
                      questionText = questionItem.question;
                      correctAnswer = questionItem.answer;
                      userAnswer = questionItem.user_answer;
                      explanation = questionItem.explanation;

                      const normUser = String(userAnswer || "")
                        .trim()
                        .toLowerCase();
                      const normCorrect = String(correctAnswer || "")
                        .trim()
                        .toLowerCase();
                      isCorrect = normUser === normCorrect;
                    } else {
                      let content = questionItem.content;
                      try {
                        if (typeof content === "string") {
                          content = JSON.parse(content);
                        }
                        if (typeof content === "string") {
                          content = JSON.parse(content);
                        }
                      } catch (e) {
                        console.error("Error parsing content:", e);
                      }

                      const type = (questionItem.type || "").toLowerCase();
                      const questionObj =
                        type === "match"
                          ? content
                          : content?.questions?.[0] || content;

                      questionText = questionObj?.question;
                      const questionId =
                        questionObj?.question_id ||
                        questionItem.sequence ||
                        questionObj?.question;

                      userAnswer = testAnswers[questionId];
                      correctAnswer =
                        questionObj?.correct_answer || questionObj?.answer;
                      explanation =
                        questionObj?.explanation || content?.explanation;

                      const normUser = String(userAnswer || "")
                        .trim()
                        .toLowerCase();
                      const normCorrect = String(correctAnswer || "")
                        .trim()
                        .toLowerCase();
                      isCorrect = normUser === normCorrect;
                    }

                    return (
                      <div key={index} className="answer-item">
                        <div className="answer-question">
                          <span className="question-number">Q{index + 1}.</span>
                          {questionText}
                        </div>
                        <div className="answer-comparison">
                          <div
                            className={`user-answer ${
                              isCorrect ? "correct" : "incorrect"
                            }`}
                          >
                            Your Answer: {String(userAnswer || "Not answered")}
                          </div>
                          <div className="correct-answer">
                            Correct Answer: {String(correctAnswer)}
                          </div>
                        </div>
                        {explanation && (
                          <div className="answer-explanation">
                            <strong>Explanation: </strong>
                            {explanation}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default TestPage;
